/**
 * City search on the same Google services as the Hub geocoder, without its country restriction:
 * the climate metrics tool compares cities anywhere, on every brand.
 */

let _autocompleteService: google.maps.places.AutocompleteService;
let _geocoder: google.maps.Geocoder;

// Created on first use, so Google's script has had a chance to load.
const autocompleteService = () => (_autocompleteService ??= new google.maps.places.AutocompleteService());
const geocoder = () => (_geocoder ??= new google.maps.Geocoder());

export interface CitySuggestion {
  placeId: string;
  name: string;
}

export interface ResolvedPlace {
  placeId: string;
  name: string;
  lat: number;
  lon: number;
}

// Google rejects a request that matches nothing with the status ZERO_RESULTS as the error's code.
const zeroResults = (e: unknown) => (e as { code?: unknown } | null)?.code === "ZERO_RESULTS";

/** Google's suggestions, none where nothing matches. Any other failure, such as a quota or network error, rejects. */
export async function suggestCities(input: string): Promise<CitySuggestion[]> {
  try {
    const { predictions } = await autocompleteService().getPlacePredictions({ input, types: ["(cities)"] });
    return predictions.map(({ description, place_id }) => ({ placeId: place_id, name: description }));
  } catch (e) {
    if (zeroResults(e)) return [];
    throw e;
  }
}

/**
 * The place's centre, or undefined where Google has none. Any other failure rejects. Google's terms
 * allow keeping a place id, but its coordinates for 30 days.
 */
export async function resolvePlace(placeId: string, name: string): Promise<ResolvedPlace | undefined> {
  try {
    const { results } = await geocoder().geocode({ placeId });
    const location = results[0]?.geometry.location;
    return location && { placeId, name, lat: location.lat(), lon: location.lng() };
  } catch (e) {
    if (zeroResults(e)) return undefined;
    throw e;
  }
}
